import { Controller } from '@hotwired/stimulus';
import { enter, leave } from 'el-transition';

export default class extends Controller {
  static targets = ['toast'];
  static values = {
    delay: { type: Number, default: 6000 },
    scrollPercentage: { type: Number, default: 15 },
    debug: { type: Boolean, default: false },
    dismissedKey: { type: String, default: 'marketingToastDismissed' },
    showWithDelay: { type: Boolean, default: true },
    showDelay: { type: Number, default: 500 }
  };

  connect() {
    if (this.debugValue)
      console.log('Toast controller connected', {
        delay: this.delayValue,
        scrollPercentage: this.scrollPercentageValue,
        debug: this.debugValue,
        dismissedKey: this.dismissedKeyValue,
        showWithDelay: this.showWithDelayValue,
        showDelay: this.showDelayValue
      });

    if (localStorage.getItem(this.dismissedKeyValue) === 'true') {
      if (this.debugValue) {
        console.log(
          'Toast was previously dismissed, but debug mode is on. Toast will not be removed, allowing further interaction for debugging.'
        );
      } else {
        this.element.remove();
        return;
      }
    }

    this.scrolledEnough = false;
    this.timeElapsed = false;
    this.toastAlreadyShown = false;

    this.handleScroll();

    if (!this.scrolledEnough) {
      this.scrollListener = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.scrollListener, { passive: true });
      if (this.debugValue) console.log('Scroll listener added');
    }

    this.timeoutId = setTimeout(() => {
      this.timeElapsed = true;
      if (this.debugValue) console.log('Time elapsed');
      this.attemptShowToast();
    }, this.delayValue);
  }

  disconnect() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
      if (this.debugValue) console.log('Scroll listener removed');
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
      if (this.debugValue) console.log('Timeout cleared');
    }
    if (this.showTimeoutId) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
      if (this.debugValue) console.log('Show timeout cleared');
    }
  }

  handleScroll() {
    if (this.scrolledEnough) return;

    const pageHeight =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight;
    let currentPercentage = 0;

    if (this.scrollPercentageValue === 0) {
      this.scrolledEnough = true;
      if (this.debugValue)
        console.log('Scroll percentage is 0, scrolledEnough set to true');
    } else if (pageHeight > 0) {
      const currentScroll = window.scrollY || window.pageYOffset;
      currentPercentage = (currentScroll / pageHeight) * 100;
      if (this.debugValue)
        console.log(
          `Current scroll: ${currentScroll}, Page height: ${pageHeight}, Scroll percentage: ${currentPercentage.toFixed(2)}%`
        );
      if (currentPercentage >= this.scrollPercentageValue) {
        this.scrolledEnough = true;
        if (this.debugValue)
          console.log(
            `Scrolled enough: ${currentPercentage.toFixed(2)}% >= ${this.scrollPercentageValue}%`
          );
      }
    } else {
      if (this.debugValue)
        console.log(
          'Page height is 0 or less. Scroll percentage might not be met unless it is 0.'
        );
    }

    if (this.scrolledEnough) {
      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
        if (this.debugValue)
          console.log('Scrolled enough, scroll listener removed.');
      }
      this.attemptShowToast();
    }
  }

  attemptShowToast() {
    if (this.debugValue) {
      console.log('Attempting to show toast:', {
        scrolledEnough: this.scrolledEnough,
        timeElapsed: this.timeElapsed,
        toastAlreadyShown: this.toastAlreadyShown,
        hasToastTarget: this.hasToastTarget,
        dismissed: localStorage.getItem(this.dismissedKeyValue) === 'true',
        debugOverrideActive: this.debugValue
      });
    }
    if (
      (localStorage.getItem(this.dismissedKeyValue) !== 'true' ||
        this.debugValue) &&
      this.scrolledEnough &&
      this.timeElapsed &&
      !this.toastAlreadyShown &&
      this.hasToastTarget
    ) {
      this.toastAlreadyShown = true;
      if (this.showWithDelayValue) {
        if (this.debugValue)
          console.log(`Showing toast with delay: ${this.showDelayValue}ms`);
        this.showTimeoutId = setTimeout(() => {
          this.show();
        }, this.showDelayValue);
      } else {
        if (this.debugValue) console.log('Showing toast immediately');
        this.show();
      }
    }
  }

  show() {
    if (this.hasToastTarget) {
      if (this.debugValue) console.log('Entering toast animation');
      enter(this.toastTarget);
    }
  }

  close() {
    if (this.hasToastTarget) {
      if (this.debugValue) console.log('Leaving toast animation');
      leave(this.toastTarget).then(() => {
        if (this.hasToastTarget) {
          this.toastTarget.remove();
          if (this.debugValue) console.log('Toast removed from DOM');
        }
      });
    }
  }

  dismiss() {
    if (this.debugValue) {
      console.log(
        'Dismissing toast (debug mode): setting localStorage key. Toast will not be closed.'
      );
    }
    localStorage.setItem(this.dismissedKeyValue, 'true');
    if (!this.debugValue) {
      this.close();
    }
  }
}
